import { useQuery } from '@tanstack/react-query';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/toast';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';
import {
  buildInvoicePdfBlob,
  buildReceiptPdfBlob,
  type BillingDocumentPdfLabels,
  type BillingInvoiceDocument,
  type BillingReceiptDocument,
} from '@/utils/billing-document-pdf';
import { formatDate, formatMoney } from '@/utils/format';
import { openBlobInNewTab } from '@/utils/open-blob-in-new-tab';

interface InvoiceRow {
  id: string;
  invoiceNumber: number;
  status: string;
  plan: string;
  totalMinor: number;
  currency: string;
  issuedAt: string;
  paidAt: string | null;
}

interface ReceiptRow {
  id: string;
  receiptNumber: number;
  invoiceNumber: number;
  amountMinor: number;
  currency: string;
  issuedAt: string;
}

type DocumentView = 'invoices' | 'receipts';
type InvoiceFilter = 'all' | 'open' | 'paid';

export const BillingDocumentsSection = () => {
  const { t } = useTranslation('billing');
  const { t: tErrors } = useTranslation('errors');
  const [view, setView] = useState<DocumentView>('invoices');
  const [invoiceTab, setInvoiceTab] = useState<InvoiceFilter>('all');
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(null);

  const pdfLabels = useCallback(
    (document: { invoiceNumber?: number; receiptNumber?: number; plan?: string; interval?: string; seatCount?: number; couponCode?: string | null }): BillingDocumentPdfLabels => ({
      merchantName: t('pdf.merchantName'),
      merchantDescriptor: t('pdf.merchantDescriptor'),
      merchantCompany: t('pdf.merchantCompany'),
      merchantContact: t('pdf.merchantContact'),
      merchantAddress: t('pdf.merchantAddress'),
      invoiceTitle: t('pdf.invoiceTitle'),
      invoiceNumber: t('pdf.invoiceNumber', { number: document.invoiceNumber ?? 0 }),
      billTo: t('pdf.billTo'),
      issued: t('pdf.issued'),
      due: t('pdf.due'),
      paid: t('pdf.paid'),
      status: t('pdf.status'),
      period: t('pdf.period'),
      description: t('pdf.description'),
      seats: t('pdf.seats'),
      amount: t('pdf.amount'),
      planLine: t('pdf.planLine', {
        plan: t(`plans.${(document.plan ?? 'essential') as 'essential'}`),
        interval: t(`pdf.intervals.${(document.interval ?? 'annual') as 'annual'}`),
      }),
      seatsLine: t('pdf.seatsLine', { count: document.seatCount ?? 0 }),
      coupon: document.couponCode ? t('pdf.coupon', { code: document.couponCode }) : '',
      subtotal: t('pdf.subtotal'),
      discount: t('pdf.discount'),
      tax: t('pdf.tax'),
      total: t('pdf.total'),
      amountPaid: t('pdf.amountPaid'),
      amountDue: t('pdf.amountDue'),
      receiptTitle: t('pdf.receiptTitle'),
      receiptNumber: t('pdf.receiptNumber', { number: document.receiptNumber ?? 0 }),
      receivedFrom: t('pdf.receivedFrom'),
      invoiceLabel: t('pdf.invoiceLabel'),
      invoiceRef: t('pdf.invoiceRef', { number: document.invoiceNumber ?? 0 }),
      amountPaidLabel: t('pdf.amountPaid'),
      subscriptionPayment: t('pdf.subscriptionPayment'),
      thankYou: t('pdf.thankYou'),
      statuses: {
        open: t('invoices.statuses.open'),
        paid: t('invoices.statuses.paid'),
        void: t('invoices.statuses.void'),
        uncollectible: t('invoices.statuses.uncollectible'),
      },
    }),
    [t],
  );

  const money = (amountMinor: number, currency: string, locale: string) =>
    formatMoney(amountMinor, currency, { locale });

  const openInvoicePdf = useCallback(
    async (row: InvoiceRow) => {
      if (openingDocumentId) return;
      setOpeningDocumentId(row.id);
      try {
        const document = await api.get<BillingInvoiceDocument>(`/billing/invoices/${row.id}`);
        const blob = await buildInvoicePdfBlob(document, pdfLabels(document), money);
        if (!openBlobInNewTab(blob)) {
          toast.error(t('history.popupBlocked'));
        }
      } catch (err) {
        toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
      } finally {
        setOpeningDocumentId(null);
      }
    },
    [openingDocumentId, pdfLabels, t, tErrors],
  );

  const openReceiptPdf = useCallback(
    async (row: ReceiptRow) => {
      if (openingDocumentId) return;
      setOpeningDocumentId(row.id);
      try {
        const document = await api.get<BillingReceiptDocument>(`/billing/receipts/${row.id}`);
        const blob = await buildReceiptPdfBlob(document, pdfLabels(document), money);
        if (!openBlobInNewTab(blob)) {
          toast.error(t('history.popupBlocked'));
        }
      } catch (err) {
        toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
      } finally {
        setOpeningDocumentId(null);
      }
    },
    [openingDocumentId, pdfLabels, t, tErrors],
  );

  const invoices = useQuery({
    queryKey: ['billing-invoices', invoiceTab],
    queryFn: () =>
      api.get<{ data: InvoiceRow[] }>('/billing/invoices', {
        query: {
          page: 1,
          pageSize: 20,
          ...(invoiceTab === 'all' ? {} : { status: invoiceTab }),
        },
      }),
    enabled: view === 'invoices',
  });

  const receipts = useQuery({
    queryKey: ['billing-receipts'],
    queryFn: () =>
      api.get<{ data: ReceiptRow[] }>('/billing/receipts', {
        query: { page: 1, pageSize: 20 },
      }),
    enabled: view === 'receipts',
  });

  const invoiceColumns: DataTableColumn<InvoiceRow>[] = [
    {
      id: 'issuedAt',
      header: t('invoices.issuedAt'),
      cell: (row) => formatDate(row.issuedAt),
    },
    {
      id: 'number',
      header: t('invoices.number'),
      cell: (row) => `#${row.invoiceNumber}`,
    },
    {
      id: 'plan',
      header: t('invoices.plan'),
      cell: (row) => t(`plans.${row.plan as 'essential'}`),
    },
    {
      id: 'total',
      header: t('invoices.total'),
      cell: (row) => formatMoney(row.totalMinor, row.currency),
    },
    {
      id: 'status',
      header: t('invoices.status'),
      cell: (row) => (
        <Badge variant={row.status === 'paid' ? 'success' : 'warning'}>
          {t(`invoices.statuses.${row.status as 'paid'}`)}
        </Badge>
      ),
    },
  ];

  const receiptColumns: DataTableColumn<ReceiptRow>[] = [
    {
      id: 'issuedAt',
      header: t('receipts.issuedAt'),
      cell: (row) => formatDate(row.issuedAt),
    },
    {
      id: 'number',
      header: t('receipts.number'),
      cell: (row) => `#${row.receiptNumber}`,
    },
    {
      id: 'invoice',
      header: t('receipts.invoice'),
      cell: (row) => `#${row.invoiceNumber}`,
    },
    {
      id: 'amount',
      header: t('receipts.amount'),
      cell: (row) => formatMoney(row.amountMinor, row.currency),
    },
  ];

  const invoiceFilterLabel = (value: InvoiceFilter) => {
    if (value === 'all') return t('invoices.tabs.all');
    if (value === 'open') return t('invoices.tabs.open');
    return t('invoices.tabs.paid');
  };

  const openingLabel = view === 'invoices' ? t('history.openingInvoice') : t('history.openingReceipt');
  const tableWithOpening = (table: ReactNode) => (
    <div className="relative" aria-busy={Boolean(openingDocumentId)}>
      {table}
      {openingDocumentId ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
          <div className="flex items-center gap-2 text-body-sm text-fg-secondary">
            <Spinner className="size-5 text-brand" />
            <span>{openingLabel}</span>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('history.title')}</CardTitle>
        <CardDescription>{t('history.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant={view === 'invoices' ? 'primary' : 'outline'}
            onClick={() => setView('invoices')}
          >
            {t('invoices.title')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={view === 'receipts' ? 'primary' : 'outline'}
            onClick={() => setView('receipts')}
          >
            {t('receipts.title')}
          </Button>
        </div>

        {view === 'invoices' ? (
          <>
            <div className="flex flex-wrap gap-2">
              {(['all', 'open', 'paid'] as const).map((value) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={invoiceTab === value ? 'secondary' : 'ghost'}
                  onClick={() => setInvoiceTab(value)}
                >
                  {invoiceFilterLabel(value)}
                </Button>
              ))}
            </div>
            {tableWithOpening(
              <DataTable
                columns={invoiceColumns}
                rows={invoices.data?.data ?? []}
                rowKey={(row) => row.id}
                loading={invoices.isLoading}
                onRowClick={(row) => void openInvoicePdf(row)}
                rowTooltip={() => t('history.openPdf')}
                empty={
                  invoiceTab === 'open'
                    ? t('invoices.emptyOpen')
                    : invoiceTab === 'paid'
                      ? t('invoices.emptyPaid')
                      : t('invoices.empty')
                }
              />,
            )}
          </>
        ) : (
          tableWithOpening(
            <DataTable
              columns={receiptColumns}
              rows={receipts.data?.data ?? []}
              rowKey={(row) => row.id}
              loading={receipts.isLoading}
              onRowClick={(row) => void openReceiptPdf(row)}
              rowTooltip={() => t('history.openPdf')}
              empty={t('receipts.empty')}
            />,
          )
        )}
      </CardContent>
    </Card>
  );
};
